import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
  Input, OnDestroy, Output, ViewChild,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { IconComponent } from './icon.component';

export interface LatLng { lat: number; lng: number; }

/** Where the map opens when no pin is known yet (central Cairo). */
export const MAP_DEFAULT_CENTER: LatLng = { lat: 30.0444, lng: 31.2357 };

/**
 * Draggable map pin (Leaflet + OpenStreetMap, loaded from the CDN in index.html).
 * Emits the pin's coordinates on drag / tap; a "locate me" button (and an optional
 * auto-locate on first show) centres the pin on the device's current location.
 */
@Component({
  selector: 'app-map-picker',
  standalone: true,
  imports: [NgIf, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div style="position:relative">
      <div #map style="width:100%;height:220px;border-radius:14px;overflow:hidden;background:var(--fr-surface-3);z-index:0"></div>
      <button type="button" (click)="locate()" [disabled]="locating" [attr.aria-label]="'Use my current location'"
        style="position:absolute;bottom:12px;inset-inline-end:12px;z-index:1000;width:42px;height:42px;border-radius:12px;border:none;cursor:pointer;background:var(--fr-surface);box-shadow:0 3px 10px rgba(0,0,0,0.22);display:flex;align-items:center;justify-content:center">
        <app-icon [name]="locating ? 'solar:refresh-linear' : 'solar:gps-bold'" [size]="21" color="var(--brand-deep)"></app-icon>
      </button>
    </div>`,
})
export class MapPickerComponent implements AfterViewInit, OnDestroy {
  /** Initial centre (defaults to central Cairo). */
  @Input() lat = MAP_DEFAULT_CENTER.lat;
  @Input() lng = MAP_DEFAULT_CENTER.lng;
  /** Try to centre on the device's current location on init. */
  @Input() useGeolocation = true;
  @Output() locationChange = new EventEmitter<LatLng>();

  @ViewChild('map') mapEl!: ElementRef<HTMLElement>;
  locating = false;
  private map: any;
  private marker: any;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    const L = (window as any).L;
    if (!L || !this.mapEl) return; // Leaflet not available → the manual area picker is the fallback

    this.map = L.map(this.mapEl.nativeElement, { attributionControl: false }).setView([this.lat, this.lng], 14);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(this.map);

    this.marker = L.marker([this.lat, this.lng], { draggable: true }).addTo(this.map);
    this.marker.on('dragend', () => this.emit());
    this.map.on('click', (e: any) => { this.marker.setLatLng(e.latlng); this.emit(); });

    // Rendered inside a sheet that animates open — recompute size after it settles.
    setTimeout(() => this.map?.invalidateSize(), 250);

    if (this.useGeolocation) this.locate();
  }

  ngOnDestroy(): void { this.map?.remove(); }

  /** Centre the map + pin on the device's current location. */
  locate(): void {
    if (!navigator.geolocation || !this.map) return;
    this.locating = true;
    this.cdr.markForCheck();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locating = false;
        const { latitude, longitude } = pos.coords;
        this.map.setView([latitude, longitude], 17);
        this.marker.setLatLng([latitude, longitude]);
        this.emit();
        this.cdr.markForCheck();
      },
      () => { this.locating = false; this.cdr.markForCheck(); }, // denied/unavailable — keep current pin
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  private emit(): void {
    const p = this.marker.getLatLng();
    this.locationChange.emit({ lat: p.lat, lng: p.lng });
  }
}
